export type TemplateSourceKind = 'document' | 'transcript' | 'surface' | 'calendar_event';

export type TemplateDiagramKind = 'mind-map' | 'process-flow' | 'concept-map';

export interface TemplateSection {
  key: string;
  heading: string;
  required: boolean;
}

export interface TemplateSummary {
  id: string;
  name: string;
  description: string;
  owner: 'nula' | 'workspace' | 'user';
  outputType: 'text' | 'canvas';
  diagramKind: TemplateDiagramKind | null;
  sections: TemplateSection[];
  sourceKinds: TemplateSourceKind[];
  lastUsedAt: string | null;
  /** The version a run started now would pin. */
  currentVersion: number;
}

export interface DocumentSourceFacts {
  hasBodyText: boolean;
  hasSurfaceText: boolean;
  finishedRecordingIds: readonly string[];
  hasPassage?: boolean;
  linkedEventIds?: readonly string[];
}

export type TemplateAvailability = { available: true } | { available: false; reason: string };

export const SUMMARY_TEMPLATE_ID = 'nula.summary';

/** Which linked meeting a template ticks by default: the one being prepared
 *  for, or the one being followed up. Absent: `upcoming`. */
export type EventPreference = 'upcoming' | 'started';

export const EVENT_PREFERENCE: Readonly<Record<string, EventPreference>> = {
  'nula.pre-meeting': 'upcoming',
  'nula.follow-up': 'started',
};

function isSatisfiable(kind: TemplateSourceKind, facts: DocumentSourceFacts): boolean {
  switch (kind) {
    case 'document':
      return facts.hasBodyText || facts.hasPassage === true;
    case 'transcript':
      return facts.finishedRecordingIds.length > 0;
    case 'surface':
      return facts.hasSurfaceText || facts.hasPassage === true;
    case 'calendar_event':
      return (facts.linkedEventIds?.length ?? 0) > 0;
  }
}

export function templateAvailability(
  t: TemplateSummary,
  facts: DocumentSourceFacts,
): TemplateAvailability {
  if (t.sourceKinds.some((kind) => isSatisfiable(kind, facts))) return { available: true };
  return { available: false, reason: unavailableReason(t.sourceKinds) };
}

/** "needs something written first" alone; otherwise "needs a, b or c". */
function unavailableReason(kinds: readonly TemplateSourceKind[]): string {
  const parts = [
    kinds.includes('calendar_event') ? 'a meeting' : null,
    kinds.includes('transcript') ? 'a recording' : null,
    kinds.some((kind) => kind === 'document' || kind === 'surface') ? 'something written' : null,
  ].filter((part): part is string => part !== null);
  if (parts.length === 1 && parts[0] === 'something written') return 'needs something written first';
  const last = parts.pop();
  return parts.length === 0 ? `needs ${last}` : `needs ${parts.join(', ')} or ${last}`;
}

const OWNER_TAGS: Record<string, string> = { nula: 'nula', workspace: 'team', user: 'mine' };

export function ownerTag(owner: string): string {
  return OWNER_TAGS[owner] ?? '';
}

/** The templates offered for *Generate from selection*, in this order. */
export const SELECTION_TEMPLATE_IDS = ['nula.summary', 'nula.study-guide', 'nula.mind-map'] as const;

export function selectionTemplates(templates: readonly TemplateSummary[]): TemplateSummary[] {
  return SELECTION_TEMPLATE_IDS.flatMap((id) => templates.filter((t) => t.id === id));
}
